import { Component } from '@theme/component';
import { morph } from '@theme/morph';

class MobileBuyBarComponent extends Component {
  requiredRefs = ['bar'];

  /** @type {boolean} */
  #userChose = false;

  /** @type {HTMLElement | null} */
  #pk = null;

  /** @type {EventListener | null} */
  #variantChangeHandler = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {Function | null} */
  #originalReplaceState = null;

  connectedCallback() {
    super.connectedCallback();

    // Intercept history.replaceState to detect sibling navigation reliably
    if (!this.#originalReplaceState) {
      this.#originalReplaceState = history.replaceState.bind(history);
      history.replaceState = (state, title, url) => {
        this.#originalReplaceState(state, title, url);

        const newHandle = url?.toString().split('/products/')?.[1]?.split('?')?.[0];
        const currentHandle = window.location.pathname.split('/products/')?.[1]?.split('?')?.[0];

        if (newHandle && newHandle !== currentHandle) {
          // URL changed to a different product — sibling navigation occurred
          setTimeout(() => {
            this.#reinitForSibling();
          }, 100);
        }
      };
    }

    this.#setupEventListeners();

    this.classList.add('is-ready');
    document.body.classList.add('eagleray-msab-active');

    this.#cacheInternalElements();
    this.#deselectWithRetry(20);
    this.#updateBar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#originalReplaceState) {
      history.replaceState = this.#originalReplaceState;
      this.#originalReplaceState = null;
    }
    this.#abortController.abort();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateBar();
  }

  #setupEventListeners() {
    const { signal } = this.#abortController;

    // Listen to variant updates anywhere on the document (bubbling up from sibling sections)
    document.addEventListener('variant:update', this.#onVariantUpdate, { signal });

    // Listen to native picker changes
    this.#pk = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    this.#variantChangeHandler = () => {
      this.#userChose = true;
      this.#updateBar();
    };
    if (this.#pk) {
      this.#pk.addEventListener('change', this.#variantChangeHandler, { signal });
    }

    // Cart events
    document.addEventListener('cart:update', this.#onCartUpdate, { signal });
    document.addEventListener('cart:error', this.#onCartError, { signal });
  }

  #reinitForSibling() {
    // 1. Remove the stale `change` listener
    if (this.#pk && this.#variantChangeHandler) {
      this.#pk.removeEventListener('change', this.#variantChangeHandler);
    }

    // 2. Re-query the new <variant-picker> element
    this.#pk = this.closest('[id^="MainProduct"]')?.querySelector('variant-picker') ?? null;
    if (!this.#pk) {
      // Fallback if not inside MainProduct
      this.#pk = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    }

    // 3. Re-bind the change listener to the new variant-picker
    if (this.#pk && this.#variantChangeHandler) {
      this.#pk.addEventListener('change', this.#variantChangeHandler);
    }

    // 4. Re-query any internal child element references
    //    Yield to the base class MutationObserver first, THEN reset UI
    requestAnimationFrame(() => {
      this.#cacheInternalElements();

      // 5. Reset UI state for the new product
      this.#deselectWithRetry(20);
      this.#updateBar();
    });
  }

  // Clicks reach handleAtcClick via the button's declarative on:click, which
  // Component delegates from document, so it survives morphs without rebinding.
  #cacheInternalElements() {
    this.refs.bar = this.querySelector('.eagleray-msab__bar');
    this.refs.atcBtn = this.querySelector('.eagleray-msab__atc');
    this.refs.atcTextEl = this.querySelector('.eagleray-msab__btn-label');
  }

  get #productId() {
    return this.dataset.productId;
  }

  get #hasSize() {
    return this.dataset.hasSize === 'true';
  }

  #picker() {
    return document.querySelector(`variant-picker[data-product-id="${this.#productId}"]`);
  }

  #productForm() {
    const mainForm = document.querySelector('form[id^="BuyButtons-ProductForm-"]');
    if (mainForm) return mainForm.closest('product-form-component') || mainForm;
    return document.querySelector(
      `[id^="MainProduct"] product-form-component[data-product-id="${this.#productId}"], ` +
      `product-form-component:not([id*="QuickAdd"])[data-product-id="${this.#productId}"]`
    );
  }

  #sizeFieldset() {
    const pk = this.#picker();
    if (!pk) return null;
    const legend = pk.querySelector('.variant-option__legend--size, .variant-option__label--size');
    return legend ? legend.closest('fieldset, .variant-option--dropdowns') : null;
  }

  #sizeRadios() {
    const fs = this.#sizeFieldset();
    return fs ? Array.from(fs.querySelectorAll('input[type="radio"]')) : [];
  }

  #checkedRadio() {
    return this.#sizeRadios().find(r => r.checked) || null;
  }

  #realAtcButton() {
    const pf = this.#productForm();
    return pf ? (pf.querySelector('[name="add"]') || pf.querySelector('[type="submit"]') || pf.querySelector('.eagleray-upsell__add-btn')) : null;
  }

  // ---- public action handlers ----

  handleAtcClick(event) {
    if (event) event.preventDefault();
    if (this.refs.atcBtn?.classList.contains('is-disabled')) return;

    // No size yet: the variant picker (snippets/variant-main-picker.liquid)
    // scrolls to the sizes and shows "Please select your size".
    if (this.#hasSize && !this.#checkedRadio()) {
      document.dispatchEvent(new CustomEvent('eagleray:require-size'));
      return;
    }

    this.#triggerAddToCart();
  }

  // ---- internal logic ----

  #triggerAddToCart() {
    const btn = this.#realAtcButton();
    if (!btn || btn.disabled) return;
    btn.dataset.puppet = 'true';
    btn.click();
    this.refs.atcBtn?.classList.add('is-added');
  }

  #updateBar() {
    const realBtn = this.#realAtcButton();
    const soldOut = !!(realBtn && realBtn.disabled);
    const chosen = this.#checkedRadio();
    const blockPurchase = this.#hasSize && chosen && soldOut;

    if (this.refs.atcBtn) {
      this.refs.atcBtn.classList.toggle('is-disabled', blockPurchase);
      if (this.refs.atcTextEl) {
        const defaultAtcLabel = this.refs.atcTextEl.dataset.defaultLabel || 'Add to Cart';
        this.refs.atcTextEl.textContent = blockPurchase
          ? (this.refs.atcBtn.getAttribute('data-soldout') || 'Sold Out')
          : defaultAtcLabel;
      }
    }
  }

  #applyNoPreselect() {
    if (!this.#hasSize || this.#userChose) return true;
    const radios = this.#sizeRadios();
    if (!radios.length) return false;

    radios.forEach(r => { if (r.checked) r.checked = false; });
    this.#updateBar();
    return true;
  }

  #deselectWithRetry(attempts) {
    if (this.#applyNoPreselect()) return;
    if (attempts <= 0) return;
    requestAnimationFrame(() => this.#deselectWithRetry(attempts - 1));
  }

  #isOurVariantEvent(e) {
    const pid = e?.detail?.data?.productId;
    return !pid || String(pid) === String(this.#productId);
  }

  #onVariantUpdate = (e) => {
    // Determine if it's a sibling product or just a variant change
    const newProductId = e.detail?.data?.newProduct?.id
                      ?? e.detail?.productId
                      ?? e.detail?.product?.id
                      ?? e.detail?.variant?.product_id;
    const currentProductId = this.#productId?.toString();

    // Update product ID if changed
    if (newProductId && newProductId.toString() !== currentProductId) {
      this.dataset.productId = newProductId.toString();
    }

    // Morph in the freshly rendered bar (keeps this component instance)
    const htmlFrag = e.detail?.data?.html;
    if (htmlFrag) {
      const newComponent = htmlFrag.querySelector('eagleray-mobile-buy-bar');
      if (newComponent) {
        if (newComponent.dataset.productId && newComponent.dataset.productId !== this.dataset.productId) {
          this.dataset.productId = newComponent.dataset.productId;
        }
        morph(this, newComponent, { childrenOnly: true });
        this.#cacheInternalElements();
      }
    }

    // Always update the bar logic based on new selected variant
    if (this.#isOurVariantEvent(e) || (newProductId && newProductId.toString() !== currentProductId)) {
      if (newProductId && newProductId.toString() !== currentProductId) {
        // Sibling product: re-bind the picker listener
        this.#reinitForSibling();
      } else {
        this.#updateBar();
      }
    }
  };

  #onCartUpdate = (e) => {
    const realBtn = this.#realAtcButton();
    if (realBtn) realBtn.dataset.puppet = 'false';

    const didError = e?.detail?.data?.didError;
    if (this.refs.atcBtn && !didError) {
      setTimeout(() => {
        this.refs.atcBtn?.classList.remove('is-added');
      }, 1600);
    } else if (this.refs.atcBtn) {
      this.refs.atcBtn.classList.remove('is-added');
    }
  };

  #onCartError = () => {
    const realBtn = this.#realAtcButton();
    if (realBtn) realBtn.dataset.puppet = 'false';
    this.refs.atcBtn?.classList.remove('is-added');
  };
}

if (!customElements.get('eagleray-mobile-buy-bar')) {
  customElements.define('eagleray-mobile-buy-bar', MobileBuyBarComponent);
}
